/**
 * Native (Tauri) API transport — the "native HttpOnly" seam (Phase 2).
 *
 * On native the WebView JS must NEVER hold the session token to build a request.
 * Every `/api/*` call is routed through the Rust `api_request` command: JS sends
 * the request WITHOUT any `Authorization`, and Rust reads the active account's
 * token from the native secure store and injects `Authorization: Bearer …`
 * server-side. The token lives only in the trusted Rust layer, exactly like the
 * web build's HttpOnly cookie.
 *
 * Web builds NEVER import this — the call site in `api.ts` is guarded by
 * {@link isTauriRuntime}, and the `@tauri-apps/api/core` import is dynamic so the
 * Tauri API is kept out of the web bundle.
 */

import { setActiveNativeToken } from "@/lib/secure-token";

/**
 * Structural `fetch` type (mirrors `packages/client`'s `ApiFetch`): we avoid
 * `typeof fetch` so this stays portable and doesn't pull in a runtime's `fetch`
 * extensions.
 */
type ApiFetch = (input: Request | string | URL, init?: RequestInit) => Promise<Response>;

/** Shape returned by the Rust `api_request` command (serde `camelCase`).*/
interface RustApiResponse {
  status: number;
  statusText: string;
  headers: [string, string][];
  bodyBase64: string;
}

/** HTTP methods that never carry a request body. */
const BODILESS_METHODS = new Set(["GET", "HEAD"]);
/** Response statuses that MUST have a null body (per the Fetch spec). */
const NULL_BODY_STATUSES = new Set([204, 205, 304]);

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCodePoint(byte);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i += 1) {
    // `atob` yields a binary string (each char is a byte 0-255, single BMP code
    // unit), so `codePointAt` never returns a surrogate/undefined here; `?? 0`
    // just satisfies its `number | undefined` type.
    bytes[i] = binary.codePointAt(i) ?? 0;
  }
  return buffer;
}

async function invokeApiRequest(
  method: string,
  url: string,
  headers: [string, string][],
  bodyBase64: string | null,
): Promise<RustApiResponse> {
  // Dynamic import keeps `@tauri-apps/api/core` out of the web bundle.
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<RustApiResponse>("api_request", { method, url, headers, bodyBase64 });
}

/**
 * Builds the native `fetch` used by the API client (and storage transports) on
 * Tauri. `getActiveTokenAccount` returns the `userId` (secure-store key) whose
 * token Rust should inject, or `null` when unauthenticated. The transport primes
 * Rust whenever the active account changes (account switch / boot), so Rust
 * always injects the CORRECT account's token — without JS ever seeing it.
 */
export function createNativeApiFetch(getActiveTokenAccount: () => string | null): ApiFetch {
  // Tracks the last account we primed Rust with. `undefined` = never primed yet
  // (distinct from `null` = explicitly unauthenticated), so the first request
  // always primes.
  let lastPrimedAccount: string | null | undefined;

  return async (input, init) => {
    // Normalise to a Request so body/headers/method resolution is uniform for
    // both `Request` inputs (openapi-fetch) and `string | URL` inputs (storage).
    const request = new Request(input, init);

    // Ensure Rust holds THIS account's token before dispatching. Re-primes only
    // on change (steady-state requests skip the secure-store round-trip).
    const account = getActiveTokenAccount();
    if (account !== lastPrimedAccount) {
      await setActiveNativeToken(account);
      lastPrimedAccount = account;
    }

    const headers: [string, string][] = [];
    request.headers.forEach((value, key) => {
      // The bearer is injected by Rust — never forward one from JS.
      if (key.toLowerCase() !== "authorization") {
        headers.push([key, value]);
      }
    });

    let bodyBase64: string | null = null;
    if (!BODILESS_METHODS.has(request.method.toUpperCase())) {
      const buffer = await request.arrayBuffer();
      if (buffer.byteLength > 0) {
        bodyBase64 = bytesToBase64(new Uint8Array(buffer));
      }
    }

    const result = await invokeApiRequest(request.method, request.url, headers, bodyBase64);

    const responseHeaders = new Headers();
    for (const [key, value] of result.headers) {
      try {
        responseHeaders.set(key, value);
      } catch {
        // Skip headers the Fetch API forbids setting from JS (e.g. `set-cookie`).
      }
    }

    const nullBody = NULL_BODY_STATUSES.has(result.status);
    // Decode into an `ArrayBuffer` — an unambiguous, non-generic `BodyInit`, so
    // we neither cast nor fight TS 5.7's generic `Uint8Array<…>` typing.
    const body =
      nullBody || result.bodyBase64 === "" ? null : base64ToArrayBuffer(result.bodyBase64);

    return new Response(body, {
      status: result.status,
      statusText: result.statusText,
      headers: responseHeaders,
    });
  };
}
